<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

defineProps({
  canRemove: { type: Boolean, default: false },
  canUpload: { type: Boolean, default: false },
  acceptedFormats: {
    type: String,
    default: '.pdf,application/pdf',
  },
  disabled: { type: Boolean, default: false },
  document: { type: Object, default: null },
  formatDescription: { type: String, default: '' },
  receiptType: { type: String, default: 'manual' },
  removing: { type: Boolean, default: false },
  showWarning: { type: Boolean, default: true },
  uploading: { type: Boolean, default: false },
});

const emit = defineEmits(['remove', 'upload']);
const { t } = useI18n();
const fileInput = ref(null);

const chooseFile = () => fileInput.value?.click();
const selectFile = event => {
  const [file] = event.target.files || [];
  if (file) emit('upload', file);
  event.target.value = '';
};
</script>

<template>
  <section
    class="rounded-lais border border-lais-border-soft bg-white p-5 shadow-lais-card sm:p-6"
  >
    <h2 class="text-base font-semibold text-lais-text">
      {{
        receiptType === 'manual'
          ? t('SALES.DELIVERY.START.UPLOAD.MANUAL_TITLE')
          : t('SALES.DELIVERY.START.UPLOAD.DOCUSIGN_TITLE')
      }}
    </h2>
    <p class="mt-2 text-sm text-lais-text-muted">
      {{ formatDescription || t('SALES.DELIVERY.START.UPLOAD.PDF_ONLY') }}
    </p>
    <div
      class="mt-5 rounded-lais-sm border border-dashed border-lais-border-soft bg-lais-bg-soft p-5"
    >
      <div
        v-if="document"
        class="flex flex-wrap items-center justify-between gap-3"
      >
        <div class="min-w-0">
          <p class="truncate text-sm font-semibold text-lais-text">
            {{ document.filename }}
          </p>
          <p class="mt-1 text-xs text-lais-text-muted">
            {{ document.content_type }}
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <LaisPrimaryButton
            v-if="canRemove"
            variant="ghost"
            size="sm"
            icon="i-lucide-trash-2"
            :is-loading="removing"
            @click="emit('remove')"
          >
            {{ t('SALES.DELIVERY.START.UPLOAD.REMOVE') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            v-if="canUpload"
            variant="ghost"
            size="sm"
            icon="i-lucide-refresh-cw"
            :is-loading="uploading"
            :disabled="disabled"
            @click="chooseFile"
          >
            {{ t('SALES.DELIVERY.START.UPLOAD.REPLACE') }}
          </LaisPrimaryButton>
        </div>
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center gap-3 py-5 text-center"
      >
        <span
          class="flex h-11 w-11 items-center justify-center rounded-full bg-white text-lais-primary shadow-sm"
        >
          <span class="i-lucide-upload text-xl" aria-hidden="true" />
        </span>
        <p class="text-sm text-lais-text-muted">
          {{ t('SALES.DELIVERY.START.UPLOAD.EMPTY') }}
        </p>
        <LaisPrimaryButton
          v-if="canUpload"
          icon="i-lucide-upload"
          :is-loading="uploading"
          :disabled="disabled"
          @click="chooseFile"
        >
          {{ t('SALES.DELIVERY.START.UPLOAD.SELECT') }}
        </LaisPrimaryButton>
      </div>
    </div>
    <p v-if="showWarning" class="mt-5 text-sm leading-6 text-lais-text-body">
      <strong>{{ t('SALES.DELIVERY.START.UPLOAD.WARNING_PREFIX') }}</strong>
      {{ t('SALES.DELIVERY.START.UPLOAD.WARNING') }}
    </p>
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      :accept="acceptedFormats"
      :disabled="disabled"
      @change="selectFile"
    />
  </section>
</template>
